// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getDatabase, connectDatabaseEmulator } from "firebase/database";
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const resolveAuthDomain = () => {
  const configuredAuthDomain = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;
  if (typeof window === 'undefined') return configuredAuthDomain;

  const host = window.location.hostname;
  const isLocalHost = host === 'localhost' || host === '127.0.0.1';
  const isFirebaseAppDomain = host.endsWith('.firebaseapp.com');

  // Keep Firebase Auth on the firebaseapp.com domain where the managed Google
  // OAuth handler is configured. The app itself may still be served from
  // web.app; those hosts should use popup flow instead of redirect flow.
  if (!isLocalHost && isFirebaseAppDomain) {
    return host;
  }

  return configuredAuthDomain;
};

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: resolveAuthDomain(),
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Toggle to enable/disable App Check easily
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY || '';
const hasAppCheckKey = Boolean(RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY !== 'RECAPTCHA_V3_SITE_KEY_PLACEHOLDER');
const ENABLE_APP_CHECK = process.env.REACT_APP_ENABLE_APP_CHECK === 'true'
  || (process.env.NODE_ENV === 'production' && hasAppCheckKey);

// Emulator configuration
const USE_EMULATORS = process.env.REACT_APP_USE_EMULATORS === 'true';
const USE_FUNCTIONS_EMULATOR = process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === 'true';
const FIRESTORE_EMULATOR_PORT = process.env.REACT_APP_FIRESTORE_EMULATOR_PORT || 8082;
const AUTH_EMULATOR_PORT = process.env.REACT_APP_AUTH_EMULATOR_PORT || 9099;
const FUNCTIONS_EMULATOR_PORT = process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || 5001;
const STORAGE_EMULATOR_PORT = process.env.REACT_APP_STORAGE_EMULATOR_PORT || 9199;
const DATABASE_EMULATOR_PORT = process.env.REACT_APP_DATABASE_EMULATOR_PORT || 9000;

// Debug logging for Firebase configuration
if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_DEBUG_FIREBASE_CONFIG === 'true') {
  console.log('Firebase Configuration Values:', {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
    useEmulators: USE_EMULATORS,
    useFunctionsEmulator: USE_FUNCTIONS_EMULATOR
  });
}

// Initialize Firebase
let app = null;
let analytics = null;
let auth = null;
let db = null;
let storage = null;
let functions = null;
let appCheck = null;
let database = null;

// Track if emulators have been connected
let emulatorsConnected = false;

const initializeFirebase = () => {
  try {
    if (!app) {
      app = initializeApp(firebaseConfig);
    }
    if (!auth) {
      auth = getAuth(app);
    }
    if (!db) {
      db = getFirestore(app);
    }
    if (!storage) {
      storage = getStorage(app);
    }
    if (!functions) {
      functions = getFunctions(app, 'us-central1');
    }
    if (!database) {
      database = getDatabase(app);
    }
    
    // Connect to emulators if enabled and not already connected
    if ((USE_EMULATORS || USE_FUNCTIONS_EMULATOR) && !emulatorsConnected) {
      try {
        if (USE_EMULATORS) {
          // Connect Auth emulator
          connectAuthEmulator(auth, `http://127.0.0.1:${AUTH_EMULATOR_PORT}`, { disableWarnings: true });
          
          // Connect Firestore emulator
          connectFirestoreEmulator(db, '127.0.0.1', parseInt(FIRESTORE_EMULATOR_PORT));
          
          // Connect Storage emulator
          connectStorageEmulator(storage, '127.0.0.1', parseInt(STORAGE_EMULATOR_PORT));
          
          // Connect Database emulator
          connectDatabaseEmulator(database, '127.0.0.1', parseInt(DATABASE_EMULATOR_PORT));
        }
        
        // Connect Functions emulator
        if (USE_FUNCTIONS_EMULATOR || USE_EMULATORS) {
          connectFunctionsEmulator(functions, '127.0.0.1', parseInt(FUNCTIONS_EMULATOR_PORT));
        }
        
        emulatorsConnected = true;
        console.log('Connected to Firebase emulators');
      } catch (error) {
        console.warn('Failed to connect to emulators:', error);
      }
    }
    
    if (!appCheck && ENABLE_APP_CHECK && hasAppCheckKey) {
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(RECAPTCHA_SITE_KEY),
        isTokenAutoRefreshEnabled: true,
      });
    }
    if (process.env.NODE_ENV === 'production' && !analytics) {
      analytics = getAnalytics(app);
    }
    return true;
  } catch (error) {
    console.error("Error initializing Firebase:", error);
    return false;
  }
};

// Initialize Firebase immediately
initializeFirebase();

export { app, analytics, auth, db, storage, functions, appCheck, database, initializeFirebase };